import { useEffect, useState, type FC } from 'react';

import { WebtextCover } from '@soomo/lib/components/WebtextCover/index';
import { type CoverManifest } from '@soomo/lib/types/WebtextManifest';
import themes, { type ThemeName } from '@soomo/lib/styles/themes/index';
import ThemeProvider from '@soomo/lib/styles/themes/ThemeProvider';
import FontProvider from '@soomo/lib/styles/themes/FontProvider';

interface Props {
	themeName?: ThemeName;
	coverManifest?: CoverManifest;
}

const DesktopPreview: FC<Props> = ({ themeName, coverManifest }) => {
	const [coverImageReady, setCoverImageReady] = useState(false);

	useEffect(() => {
		if (coverManifest?.webtext_cover_background_image_url) {
			setCoverImageReady(false);
			const img = new Image();
			img.onload = () => setCoverImageReady(true);
			img.onerror = () => setCoverImageReady(true);
			img.src = coverManifest.webtext_cover_background_image_url;
		} else {
			setCoverImageReady(true);
		}
	}, [coverManifest]);

	if (!themeName || !coverManifest) {
		return (
			<div id="desktop-preview" key="default">
				<img
					id="desktop-preview-shell"
					className="fade-in"
					src="https://s3.amazonaws.com/assets.webtexts.com/signup/laptop_shell.png"
				/>
				<img
					className="fade-in"
					id="desktop-preview-img"
					src="https://s3.amazonaws.com/assets.webtexts.com/signup/default_textbook_image_desktop_2026_02_20.png"
				/>
			</div>
		);
	}

	return (
		<div
			id="desktop-preview"
			key="cover-manifest-available"
			style={{ opacity: coverImageReady ? 1 : 0, transition: 'opacity 300ms linear' }}>
			<svg
				viewBox="0 0 1000 700"
				width="100%"
				height="auto"
				xmlns="http://www.w3.org/2000/svg"
				role="img"
				aria-label="Laptop with overlay via SVG">
				{/* raster laptop image that scales with SVG */}
				<image
					href="data:image/png;base64,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"
					x="0"
					y="0"
					width="1000"
					height="700"
					preserveAspectRatio="xMidYMid meet"
				/>

				{/* foreignObject placed where the laptop screen is inside the image. */}
				{/* These numbers (x, y, width, height) must be tuned to your image. */}
				{/* Example values assume the screen area starts at x=190,y=140 with 620x360 size */}
				<foreignObject x="120" y="100" width="760" height="480">
					<div
						// @ts-ignore - xmlns is required for Safari foreignObject support
						xmlns="http://www.w3.org/1999/xhtml"
						style={styles}
						role="document"
						aria-label="Screen content">
						<ThemeProvider theme={themes[themeName]}>
							<FontProvider>
								<WebtextCover coverManifest={coverManifest} />
							</FontProvider>
						</ThemeProvider>
					</div>
				</foreignObject>
			</svg>
		</div>
	);
};

const styles = {
	width: '100%',
	height: '100%',
	containerType: 'size'
};

export default DesktopPreview;
